import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPatientStatsHistoryService } from "../api/services";
import { useRequestError } from "../hooks/useRequestError";
import type { PrescriptionStatModel, VitalFieldSource } from "../types/models";
import { getDate, getTimeAgo } from "../utils/date";

type HistoryItem = {
  id?: string;
  sleep_hours?: number;
  steps_count?: number;
  blood_systolic_pressure?: number;
  blood_diastolic_pressure?: number;
  blood_glucose?: number;
  heart_rate?: number;
  prescription?: PrescriptionStatModel[];
  field_sources?: Record<string, VitalFieldSource>;
  created_at?: string;
  updated_at?: string;
};

const SOURCE_LABELS: Record<string, string> = {
  manual: "Manual",
  apple_health: "Apple Health",
  google_fit: "Google Fit",
  samsung_health: "Samsung Health",
  accu_check: "Accu-Chek",
  fhir_hospital: "Hospital FHIR",
  hl7_hospital: "Hospital HL7",
  whoop: "WHOOP",
};

const formatSources = (fieldSources?: Record<string, VitalFieldSource>) => {
  if (!fieldSources) {
    return null;
  }
  const labels = Array.from(
    new Set(
      Object.values(fieldSources)
        .map((item) => item?.source_system)
        .filter(Boolean)
        .map((key) => SOURCE_LABELS[key as string] || String(key))
    )
  );
  return labels.length ? labels.join(", ") : null;
};

export const VitalsHistoryPage = () => {
  const { userId = "" } = useParams();
  const navigate = useNavigate();
  const { handleRequestError } = useRequestError();
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getPatientStatsHistoryService(userId);
        const list = Array.isArray(res)
          ? res
          : (res as { result_list?: HistoryItem[] })?.result_list || [];
        setItems(list);
      } catch (error) {
        handleRequestError(error);
      } finally {
        setLoading(false);
      }
    };
    if (userId) load();
  }, [userId, handleRequestError]);

  return (
    <div className="page">
      <header className="page-header">
        <h1>Vitals History</h1>
        <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
          Back
        </button>
      </header>
      {loading ? <div className="spinner" /> : null}
      {!loading && items.length === 0 ? (
        <div className="empty-state">No history available</div>
      ) : (
        <div className="card-list">
          {items.map((item, index) => {
            const sources = formatSources(item.field_sources);
            const prescriptions = Array.isArray(item.prescription)
              ? item.prescription
              : [];
            return (
              <div key={item.id || index} className="panel stack gap-md">
                <div className="row-between">
                  <strong>
                    {item.updated_at || item.created_at
                      ? getDate(item.updated_at || item.created_at)
                      : `Entry ${index + 1}`}
                  </strong>
                  <span className="muted">
                    {item.updated_at || item.created_at
                      ? getTimeAgo(item.updated_at || item.created_at || "")
                      : ""}
                  </span>
                </div>
                {sources ? <p className="muted">Source: {sources}</p> : null}
                <div>
                  <h3>Lifestyle</h3>
                  <div className="detail-grid">
                    <div>Steps: {item.steps_count ?? "—"}</div>
                    <div>Sleep Hours: {item.sleep_hours ?? "—"}</div>
                  </div>
                </div>
                <div>
                  <h3>Vitals</h3>
                  <div className="detail-grid">
                    <div>Systolic Pressure: {item.blood_systolic_pressure ?? "—"}</div>
                    <div>Diastolic Pressure: {item.blood_diastolic_pressure ?? "—"}</div>
                    <div>Blood Glucose: {item.blood_glucose ?? "—"}</div>
                    <div>Heart Rate: {item.heart_rate ?? "—"}</div>
                  </div>
                </div>
                {prescriptions.length > 0 ? (
                  <div>
                    <h3>Prescription</h3>
                    <ul className="plain-list">
                      {prescriptions.map((rx, rxIndex) => (
                        <li key={`${rx.name}-${rxIndex}`}>
                          {rx.name}: {rx.taken ? "Taken" : "Not Taken"}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
